import {Reactive, reactive} from "vue";
import {Subscription} from "./Subscription.ts";
import {CacheManager} from "../cache/CacheManager.ts";

/* see docs/EXTRACTED_COMMENTS.md#c710 */
export type ChannelSyncState = "bootstrapping" | "pending" | "finalizing" | "live";

export class Channel {
    private readonly name: string;
    private rows: Reactive<any[]>;
    private expected_rows: number;
    private readonly subscriptions: Map<string, Subscription>;
    private syncState: ChannelSyncState = "bootstrapping";
    // see docs/EXTRACTED_COMMENTS.md#c741
    private lastActivity: number = Date.now();

    constructor(name: string) {
        this.name = name;
        this.rows = reactive([]);
        this.subscriptions = new Map<string, Subscription>();
        this.expected_rows = -1;
    }

    /** Frozen fan-out: bootstrapping or pending — anything but live. */
    isPending(): boolean {
        return this.syncState !== "live";
    }

    /** Inline fast path eligible: no queued ops can exist for this channel. */
    isBootstrapping(): boolean {
        return this.syncState === "bootstrapping";
    }

    markPending(): void {
        if (this.syncState === "live") this.touch();
        this.syncState = "pending";
    }

    /** Pending, with a ChannelSync finalize hop already queued. */
    isFinalizeQueued(): boolean {
        return this.syncState === "finalizing";
    }

    markFinalizing(): void {
        this.syncState = "finalizing";
    }

    markBootstrapping(): void {
        if (this.syncState === "live") this.touch();
        this.syncState = "bootstrapping";
    }

    markLive(): void {
        this.syncState = "live";
    }

    /* see docs/EXTRACTED_COMMENTS.md#c741 */
    touch(): void {
        this.lastActivity = Date.now();
    }

    getLastActivity(): number {
        return this.lastActivity;
    }

    getName(): string {
        return this.name;
    }

    getRows(): Reactive<any[]> {
        return this.rows;
    }

    setRows(rows: any[], markDirty: boolean = false) {
        // see docs/EXTRACTED_COMMENTS.md#c007
        if (rows !== this.rows) {
            this.rows.length = 0;
            for (const row of rows) {
                this.rows.push(row);
            }
        }
        if (markDirty) {
            CacheManager.instance.markDirty();
        }
    }
    getSubscriptions(): Map<string, Subscription> {
        return this.subscriptions;
    }

    addSubscription(id: string, subscription: Subscription) {
        this.subscriptions.set(id, subscription);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c008 */
    clearSubscriptions(): void {
        for (const sub of this.subscriptions.values()) {
            try {
                sub.dispose?.();
            } catch (e) {
                console.warn("[Channel] subscription dispose threw", e);
            }
        }
        this.subscriptions.clear();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c009 */
    removeSubscription(id: string): boolean {
        const sub = this.subscriptions.get(id);
        if (!sub) return false;
        this.subscriptions.delete(id);
        try {
            sub.dispose?.();
        } catch (e) {
            console.warn(`[Channel] subscription dispose threw for ${id}`, e);
        }
        return true;
    }

    public hasExpectedRows() {
        return this.expected_rows > 0;
    }

    public setExpectedRows(rows_expected: number) {
        this.expected_rows = rows_expected;
    }

    public getExpectedRows() {
        return this.expected_rows;
    }
}
